import React, { useState, useEffect, useMemo } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  HStack,
  Text,
  Checkbox,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Box,
  useToast,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Icon,
} from "@chakra-ui/react";
import { BsX, BsCheck } from "react-icons/bs";

interface PartQuantity {
  partNumber: string;
  rootPartNumber: string;
  quantity: number;
  partName?: string;
}

interface SelectedPart {
  partNumber: string;
  rootPartNumber: string;
  quantity: number;
  partName?: string;
}

interface PartSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedParts: SelectedPart[]) => void;
  availableParts: PartQuantity[];
  supplierName: string;
  modalTitle?: string;
}

const PartSelectionModal: React.FC<PartSelectionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  availableParts,
  supplierName,
  modalTitle = "Select Parts to Allocate",
}) => {
  const toast = useToast();
  const [selectedParts, setSelectedParts] = useState<
    Map<string, { selected: boolean; quantity: number }>
  >(new Map());

  useEffect(() => {
    if (isOpen) {
      const initialSelection = new Map<
        string,
        { selected: boolean; quantity: number }
      >();
      availableParts.forEach((part) => {
        initialSelection.set(part.rootPartNumber, {
          selected: true,
          quantity: part.quantity,
        });
      });
      setSelectedParts(initialSelection);
    }
  }, [isOpen, availableParts]);

  const handleCheckboxChange = (rootPartNumber: string, checked: boolean) => {
    setSelectedParts((prev) => {
      const newMap = new Map(prev);
      const current = newMap.get(rootPartNumber);
      if (current) {
        newMap.set(rootPartNumber, { ...current, selected: checked });
      }
      return newMap;
    });
  };

  const handleQuantityChange = (rootPartNumber: string, value: string) => {
    const numValue = parseInt(value, 10);
    if (isNaN(numValue) || numValue < 0) return;

    const part = availableParts.find(
      (p) => p.rootPartNumber === rootPartNumber
    );
    if (!part) return;
    if (numValue > part.quantity) {
      toast({
        title: "Invalid Quantity",
        description: `Quantity cannot exceed ${part.quantity}`,
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    setSelectedParts((prev) => {
      const newMap = new Map(prev);
      const current = newMap.get(rootPartNumber);
      if (current) {
        newMap.set(rootPartNumber, { ...current, quantity: numValue });
      }
      return newMap;
    });
  };

  const handleRowClick = (rootPartNumber: string, event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    if (
      target.closest('input[type="checkbox"]') ||
      target.closest('[role="checkbox"]') ||
      (target.tagName === "INPUT" &&
        (target as HTMLInputElement).type === "checkbox")
    ) {
      return;
    }
    const selection = selectedParts.get(rootPartNumber);
    handleCheckboxChange(rootPartNumber, !selection?.selected);
  };

  const getSelectedPartsArray = (): SelectedPart[] => {
    const result: SelectedPart[] = [];
    availableParts.forEach((part) => {
      const selection = selectedParts.get(part.rootPartNumber);
      if (selection && selection.selected && selection.quantity > 0) {
        result.push({
          partNumber: part.partNumber,
          rootPartNumber: part.rootPartNumber,
          quantity: selection.quantity,
          partName: part.partName,
        });
      }
    });
    return result;
  };

  const handleConfirm = () => {
    const selected = getSelectedPartsArray();

    if (selected.length === 0) {
      toast({
        title: "No Parts Selected",
        description:
          "Please select at least one part with more than 0 quantity to Assign ",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    onConfirm(selected);
    onClose();
  };

  const allSelected = useMemo(() => {
    let selectedCount = 0;
    selectedParts.forEach((value) => {
      if (value.selected) selectedCount++;
    });
    return selectedCount === availableParts.length && availableParts.length > 0;
  }, [selectedParts, availableParts.length]);

  const handleMasterCheckboxChange = (checked: boolean) => {
    setSelectedParts((prev) => {
      const newMap = new Map(prev);
      availableParts.forEach((part) => {
        newMap.set(part.rootPartNumber, {
          selected: checked,
          quantity: checked ? part.quantity : 0,
        });
      });
      return newMap;
    });
  };

  const selectedCount = useMemo(() => {
    let count = 0;
    selectedParts.forEach((value) => {
      if (value.selected) count++;
    });
    return count;
  }, [selectedParts]);

  const totalQuantity = useMemo(() => {
    let total = 0;
    availableParts.forEach((part) => {
      const selection = selectedParts.get(part.rootPartNumber);
      if (selection && selection.selected) {
        total += selection.quantity;
      }
    });
    return total;
  }, [availableParts, selectedParts]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="4xl"
      scrollBehavior="inside"
      isCentered
    >
      <ModalOverlay />
      <ModalContent
        borderRadius="xl"
        overflow="hidden"
        maxHeight="85vh"
        display="flex"
        flexDirection="column"
        boxShadow="md"
      >
        <Box
          bgGradient="linear(to-r, #1976f2, #5b2be0)"
          px={6}
          py={4}
          position="relative"
          borderTopRadius="xl"
        >
          <ModalCloseButton
            color="white"
            top={3}
            right={4}
            _hover={{
              background: "white",
              color: "blue.500",
            }}
          />
          <VStack align="start" spacing={2} pr={10}>
            <Text fontSize="lg" fontWeight="bold" color="white">
              {modalTitle}
            </Text>
            <HStack spacing={4}>
              <Text
                fontSize="sm"
                fontWeight="500"
                color="rgba(255, 255, 255, 0.64)"
              >
                Supplier: {supplierName}
              </Text>
              <HStack spacing={2}>
                <Badge
                  bg="rgba(255, 255, 255, 0.2)"
                  color="white"
                  fontSize="xs"
                  px={2}
                  py={1}
                  borderRadius="full"
                >
                  {selectedCount} parts
                </Badge>
                <Badge
                  bg="rgba(255, 255, 255, 0.2)"
                  color="white"
                  fontSize="xs"
                  px={2}
                  py={1}
                  borderRadius="full"
                >
                  Total Qty: {totalQuantity}
                </Badge>
              </HStack>
            </HStack>
          </VStack>
        </Box>
        <ModalBody
          px={6}
          py={4}
          flex={1}
          overflowY="auto"
          bg="#fbfbfb"
          fontFamily="'Nunito','Inter', sans-serif"
          sx={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          <Box
            border="1px solid"
            borderColor="gray.200"
            borderRadius="lg"
            boxShadow="xs"
            p={4}
            bg="white"
          >
            <Box
              maxHeight="60vh"
              overflowY="auto"
              overflowX="auto"
              onWheel={(e) => e.stopPropagation()}
            >
              <Table
                variant="simple"
                size="sm"
                style={{ tableLayout: "fixed" }}
              >
                <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
                  <Tr>
                    <Th width="40px">
                      <Checkbox
                        isChecked={allSelected}
                        onMouseDown={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleMasterCheckboxChange(e.target.checked);
                        }}
                        colorScheme="blue"
                      />
                    </Th>
                    <Th>Part Information</Th>
                    <Th textAlign="center" width="150px">
                      Assign Qty
                    </Th>
                    <Th textAlign="right" width="120px">
                      Remaining Qty
                    </Th>
                  </Tr>
                </Thead>

                <Tbody>
                  {availableParts.map((part) => {
                    const selection = selectedParts.get(part.rootPartNumber);
                    const isSelected = selection?.selected || false;
                    const quantity = selection?.quantity || 0;

                    return (
                      <Tr
                        key={part.rootPartNumber}
                        _hover={{ bg: "gray.50", cursor: "pointer" }}
                        onClick={(e) => handleRowClick(part.rootPartNumber, e)}
                        bg={isSelected ? "blue.50" : "white"}
                      >
                        <Td
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            isChecked={isSelected}
                            onChange={(e) => {
                              handleCheckboxChange(
                                part.rootPartNumber,
                                e.target.checked
                              );
                            }}
                            colorScheme="blue"
                          />
                        </Td>

                        <Td>
                          <VStack align="start" spacing={0}>
                            <Text
                              fontWeight="600"
                              fontSize="sm"
                              whiteSpace="nowrap"
                            >
                              {part.partNumber || part.rootPartNumber}
                            </Text>
                            {part.partName && (
                              <Text
                                fontSize="xs"
                                color="gray.600"
                                whiteSpace="nowrap"
                              >
                                {part.partName}
                              </Text>
                            )}
                          </VStack>
                        </Td>

                        <Td textAlign="center">
                          <NumberInput
                            value={quantity}
                            onChange={(value) =>
                              handleQuantityChange(part.rootPartNumber, value)
                            }
                            onClick={(e) => e.stopPropagation()}
                            min={0}
                            max={part.quantity}
                            isDisabled={!isSelected}
                            size="sm"
                            maxW="100px"
                            mx="auto"
                          >
                            <NumberInputField />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                        </Td>

                        <Td textAlign="right">
                          <Text fontSize="sm" fontWeight="600" color="gray.600">
                            {part.quantity}
                          </Text>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          </Box>
        </ModalBody>
        <ModalFooter
          bg="#fffdf9"
          borderTop="1px solid"
          borderColor="gray.200"
          justifyContent="flex-end"
          gap={3}
          position="sticky"
          bottom={0}
          zIndex={1}
        >
          <Button
            variant="ghost"
            color="gray.700"
            leftIcon={<Icon as={BsX} />}
            onClick={onClose}
            _hover={{
              bg: "gray.100",
              color: "gray.800",
            }}
          >
            Cancel
          </Button>
          <Button
            backgroundSize="200%"
            transition="background-position 0.3s ease"
            backgroundPosition="left center"
            color="white"
            bgGradient="linear(to-r,#5b2be0, #1976f2, #5b2be0)"
            leftIcon={<Icon as={BsCheck} />}
            onClick={handleConfirm}
            isDisabled={selectedCount === 0}
            _hover={{
              backgroundPosition: "right center",
            }}
          >
            Add to Bucket
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PartSelectionModal;
